import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import { useDispatch, useSelector } from "react-redux";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";

// --- New: Import react-select ---
import Select from "react-select";
import { updateUserPreference } from "../../../slices/thunks";
import ChatHistoryAIChatModal from "../../../Components/Common/ChatHistoryAIChatModal";
import TransactionsModal from "../../../Components/Common/TransactionsModal";
import { fetchUserPaymentHistory, fetchUserPaymentReportTableColumnNames, fetchUserWithTotalPayment } from "../../../slices/userPayments/thunk";

const UserPayments = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState("");
    const [selectedSession, setSelectedSession] = useState(null); 
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const today = new Date().toISOString().split("T")[0];

    const actionConfig = {
        showCheckboxColumn: false,
        action: false
    };

    const paymentHistoryColumns = [
        {
            "ID": 1,
            "TitleEn": "TransactionID",
            "TitleAr": "TransactionID",
            "ValueEn": "Transaction ID",
            "ValueAr": "معرف المعاملة",
            "FieldName": "Normal",
            "ModuleName": "USERPAYMENTSHISTORY",
            "PageID": 19,
            "Title": "TransactionID",
            "RoleID": [1],
        },
        {
            "ID": 2,
            "TitleEn": "Amount",
            "TitleAr": "Amount",
            "ValueEn": "Amount",
            "ValueAr": "المبلغ",
            "FieldName": "Normal",
            "ModuleName": "USERPAYMENTSHISTORY",
            "PageID": 19,
            "Title": "Amount",
            "RoleID": [1]
        },
        {
            "ID": 3,
            "TitleEn": "Status",
            "TitleAr": "Status",
            "ValueEn": "Status",
            "ValueAr": "الحالة",
            "FieldName": "Normal",
            "ModuleName": "USERPAYMENTSHISTORY",
            "PageID": 19,
            "Title": "Status",
            "RoleID": [1]
        },
        {
            "ID": 4,
            "TitleEn": "CreatedDate",
            "TitleAr": "CreatedDate",
            "ValueEn": "Created Date",
            "ValueAr": "تاريخ الإنشاء",
            "FieldName": "Time",
            "ModuleName": "USERPAYMENTSHISTORY",
            "PageID": 19,
            "Title": "CreatedDate",
            "RoleID": [1]
        }
    ];

    const permission = (() => {
         return usePermissions(3, 19);
    })();

    const { tableColumnNames, loading, headerLoading, userPayments, paymentHistory, paymentHistoryLoading } = useSelector(
        (state) => ({
            tableColumnNames: state.UserPayments.tableColumnNames,
            loading: state.UserPayments.loading,
            headerLoading: state.UserPayments.headerLoading,
            userPayments: state.UserPayments.userPayments,
            paymentHistory: state.UserPayments.paymentHistory,
            paymentHistoryLoading: state.UserPayments.paymentHistoryLoading,
        })
    );

    const handleSearch = (e) => {
        e.preventDefault();

        const payload = {};
        if (fromDate) payload.FromDate = fromDate;
        if (toDate) payload.ToDate = toDate;
        if (status) payload.Status = status;

        dispatch(fetchUserWithTotalPayment(payload));
    };

    const handleClear = (e) => {
        e.preventDefault();
        setFromDate("");
        setToDate("");
        dispatch(fetchUserWithTotalPayment());
    };

    const handleViewHistory = (session) => {
        dispatch(fetchUserPaymentHistory({UserID: session.ID}))
        setSelectedSession(session);
        setHistoryModalOpen(true);
    };

    const toggleHistoryModal = () => {
        setHistoryModalOpen(false);
        setSelectedSession(null); // Clear data when closing
    };

    const handleViewFeedback = (message) => {
        setFeedbackMessage(message);
        setFeedbackModalOpen(true);
    };

    const toggleFeedbackModal = () => {
        setFeedbackModalOpen(false);
        setFeedbackMessage("");
    };
    
    const saveUserPreferences = async () => {
        const preferencesPayload = {
        ColumnIDs: tableColumnNames.reduce((acc, column) => {
            acc[column.ID] = selectedColumns.includes(column.ID);
            return acc;
        }, {}),
        };

        setModalOpen(false);
        try {
        await dispatch(updateUserPreference(preferencesPayload)).unwrap();
        await dispatch(
            fetchUserPaymentReportTableColumnNames({
            module: "USERPAYMENTS",
            })
        );

        } catch (error) {
        console.error("Error updating preferences:", error);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch(fetchUserWithTotalPayment()); 
                dispatch(fetchUserPaymentReportTableColumnNames({ module: "USERPAYMENTS" }));
            } catch (error) {
                console.error(error);
            }   
        };

        fetchData();
    }, [dispatch]);

    document.title = "Alitqan | AI Chat Report";

    return (
        <React.Fragment>
        <div className="page-content">
            <Container fluid={true}>
            <Row className="mb-3">
                <Col>
                    <BreadCrumb title={t("UserPayments")} />
                </Col>
            </Row>

            <Row>
                <Col lg={12}>
                <Card>
                    <CardHeader>
                    <h5 className="card-title mb-0">{t("SearchFilters")}</h5>
                    </CardHeader>
                    <CardBody>
                    <Form onSubmit={handleSearch}>
                        <Row className="g-3">

                        <Col md={4}>
                            <Label>{t("DateFromTo")}</Label>
                            <div className="d-flex gap-2">
                                {/* // --- Modified: Added value and onChange --- */}
                                <Input type="date" name="dateFrom" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={today} />
                                <Input type="date" name="dateTo" value={toDate} onChange={(e) => setToDate(e.target.value)} max={today} />
                            </div>
                        </Col>
                        
                        <Col md={12} className="text-end ">
                            {/* // --- Modified: Added onClick handlers --- */}
                            <Button type="submit" color="primary" className="me-2">
                                <i className="ri-search-line align-bottom me-1"></i> {t("Search")}
                            </Button>
                            <Button color="light" className="me-2" onClick={handleClear}>
                                <i className="ri-close-circle-line align-bottom me-1"></i> {t("Clear")}
                            </Button>
                        </Col>
                        </Row>
                    </Form>
                    </CardBody>
                </Card>
                </Col>
            </Row>

            <div className="table-responsive">
                <Card>
                    <CardHeader>
                        <Row className="g-4 ">
                            <Col className="col-sm-auto">
                            <div>
                                <h5 className="card-title mb-0">{t("UserPayments")}</h5>
                            </div>
                            </Col>
                            <Col className="col-sm">
                            <div className="d-flex justify-content-sm-end">
                                <Button
                                    className="btn btn-soft"
                                    onClick={() => setModalOpen(true)}
                                    disabled={loading || headerLoading}
                                >
                                <i className="ri-filter-3-line"></i>
                                </Button>
                                <div className="search-box ms-2 me-2">
                                <Input
                                    type="text"
                                    className="form-control search"
                                    placeholder={t("Search here")}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <i className="ri-search-line search-icon"></i>
                                </div>
                            </div>
                            </Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        {   (loading || headerLoading) ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <Spinner style={{ height: "3rem", width: "3rem" }} className="me-2">
                            Loading...
                            </Spinner>
                        </div>
                        ) : (
                        <div className="listjs-table" id="customerList">
                            <CommonDataTable
                            data={userPayments}
                            tableColumnNames={tableColumnNames}
                            searchTerm={searchTerm}
                            actionConfig={actionConfig}
                            permission={permission}
                            handleButtonClick={handleViewHistory}
                            />
                        </div>
                        )}
                    </CardBody>
                </Card>
            </div>
            </Container>

            <ColumnSelectionModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(!modalOpen)}
                columns={tableColumnNames}
                selectedColumns={selectedColumns}
                setSelectedColumns={setSelectedColumns}
                handleSave={saveUserPreferences}
            />

            <TransactionsModal
                isOpen={historyModalOpen}
                toggle={toggleHistoryModal}
                title={selectedSession ? `${t("PaymentHistory")} - ${selectedSession.UserName}` : t("PaymentHistory")}
                data={paymentHistory}
                tableColumnNames={paymentHistoryColumns}
                loading={paymentHistoryLoading}
                permission={permission}
            />
        </div>
        </React.Fragment>
    );
};

export default UserPayments;